import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { listStrategies, listSessions, switchSessionStrategy } from "@/api/alpha";
import { api } from "@/api/client";
import DecisionFeed from "./components/DecisionFeed";
import { useSubscription } from "@/hooks/useSubscription";
import PaywallModal from "@/components/feature/PaywallModal";

const FILTERS = ["全部", "稳健", "均衡", "激进", "事件"] as const;
const STYLE_TAGS: Record<string, string> = { stable: "稳健", balanced: "均衡", aggressive: "激进", event: "事件", index: "指数增强", ai: "智能体" };

export default function AIStrategy() {
  const navigate = useNavigate();
  const sub = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [cards, setCards] = useState<any[]>([]);
  const [filter, setFilter] = useState("全部");
  const [loading, setLoading] = useState(true);
  const [currentName, setCurrentName] = useState("");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [switching, setSwitching] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ strategies }, sess] = await Promise.all([
          listStrategies(),
          listSessions().catch(() => ({ sessions: [], total: 0 })),
        ]);
        if (!alive) return;
        const running = sess.sessions.find((s: any) => s.status === "running") || sess.sessions[0];
        if (running) { setCurrentName(running.strategyName); setSessionId(running.id); }
        setCards(strategies.map((st: any) => ({
          id: st.id, name: st.name, style: STYLE_TAGS[st.style] || st.style,
          tags: st.tags, annualReturn: st.annualReturn,
          maxDrawdown: -Math.abs(st.maxDrawdown),
          holdingPeriodDays: st.holdingPeriodDays, userCount: st.userCount,
          isCurrent: running ? st.id === running.strategyId : false,
          slug: st.slug,
        })));
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  const handleSwitch = async (strategyId: number) => {
    setSwitching(strategyId);
    try {
      if (sessionId) {
        await switchSessionStrategy(sessionId, { strategyId });
      } else {
        // No running session — create one
        await api.post("/alpha-lab/sessions", { strategyId, fundAmount: 500000 });
      }
      setSwitching(null);
      window.location.reload();
    } catch {
      setSwitching(null);
      alert("切换失败，请重试");
    }
  };

  const filtered = filter === "全部" ? cards : cards.filter((c: any) => c.style === filter || c.tags?.includes(filter));

  return (
    <div className="flex-1 flex flex-col">
      <div className="px-4 pt-4 pb-3">
        <h1 className="text-lg font-bold text-foreground-950">AI 策略</h1>
      </div>

      {currentName && (
        <div className="mx-4 mb-2 p-3 rounded-xl bg-primary-50 border border-primary-200/60">
          <span className="text-xs font-semibold text-foreground-950">当前策略: {currentName}</span>
        </div>
      )}

      <div className="mx-4 mb-3">
        <DecisionFeed sessionId={sessionId} sessionName={currentName} />
      </div>

      <div className="flex gap-2 px-4 mb-3 overflow-x-auto scrollbar-hide">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-[11px] px-3 py-1 rounded-full font-medium whitespace-nowrap cursor-pointer transition-colors ${filter === f ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="flex-1 px-4 pb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-foreground-400">{filtered.length} 个策略</span>
            <button onClick={() => sub.subscribed ? navigate("/ai-strategy/create") : setShowPaywall(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 text-white text-[11px] font-medium cursor-pointer">
              <i className="ri-add-line text-xs" /> {sub.subscribed ? "新建策略" : "开通新建"}
            </button>
          </div>
          <div className="space-y-3">
          {filtered.length === 0 && <div className="text-center py-12"><p className="text-sm text-foreground-400">暂无策略</p></div>}
          {filtered.map((c: any) => (
            <div key={c.id} className="bg-white rounded-xl p-4 border border-background-200/60">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-950">{c.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">{c.style}</span>
                    {c.isCurrent && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-600">当前</span>}
                  </div>
                  <div className="text-[10px] text-foreground-400 mt-0.5">{c.tags}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-[11px] mb-3">
                <span className="text-[#E11D48] font-semibold">年化 +{c.annualReturn}%</span>
                <span className="text-[#10B981] font-semibold">回撤 {c.maxDrawdown}%</span>
                <span className="text-foreground-400">{c.holdingPeriodDays}天</span>
              </div>
              <div className="flex gap-2">
                {!c.isCurrent && (
                  <button onClick={() => handleSwitch(c.id)} disabled={switching === c.id}
                    className="flex-1 py-2 rounded-lg text-[11px] font-semibold bg-primary-500 text-white cursor-pointer disabled:opacity-50">
                    {switching === c.id ? "切换中..." : "使用此策略"}
                  </button>
                )}
                <button onClick={() => navigate(`/ai-strategy/detail/${c.id}`)} className="flex-1 py-2 rounded-lg text-[11px] font-medium bg-background-100 text-foreground-600 cursor-pointer">详情</button>
                <button onClick={() => navigate(`/ai-strategy/backtest/${c.id}`)} className="py-2 px-3 rounded-lg text-[11px] font-medium bg-background-100 text-foreground-500 cursor-pointer">回测</button>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}
      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} description="AI 策略功能需要联系客服开通" />
    </div>
  );
}
